// /app/frontend/src/components/frugal-v2/sections/IncomesSection.jsx
// Incomes section - shows account selector when marking as received

import React, { useState } from 'react';
import { DollarSign } from 'lucide-react';
import CollapsibleSection from '../components/CollapsibleSection';
import ListItem from '../components/ListItem';
import AddItemForm from '../components/AddItemForm';
import EditItemModal from '../components/EditItemModal';
import AccountSelector from '../components/AccountSelector';
import { INCOME_CATEGORIES } from '../constants/categories';

const IncomesSection = ({ incomes = [], onAdd, onToggle, onEdit, onDelete, token, t }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [pendingToggle, setPendingToggle] = useState(null);

  const total = incomes.reduce((sum, i) => sum + (i.amount || 0), 0);
  const received = incomes
    .filter(i => i.status === 'paid')
    .reduce((sum, i) => sum + (i.amount || 0), 0);

  const subtitle = `${t('frugal.incomes.received')}: $${received.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleToggle = (id) => {
    const income = incomes.find(i => i.id === id);
    if (!income) return;

    if (income.status === 'paid') {
      // Undoing - no need to ask for account
      onToggle(id, null);
    } else {
      // Marking as received - ask which account
      setPendingToggle(income);
    }
  };

  const handleAccountSelect = (account) => {
    if (pendingToggle) {
      onToggle(pendingToggle.id, account?.id || null);
      setPendingToggle(null);
    }
  };

  const handleSaveEdit = async (id, data) => {
    if (onEdit) await onEdit(id, data);
    setEditingItem(null);
  };

  const addFormComponent = (
    <AddItemForm
      type="income"
      onAdd={async (data) => {
        await onAdd(data);
        setShowForm(false);
      }}
      onCancel={() => setShowForm(false)}
      colorClass="bg-emerald-500 hover:bg-emerald-600"
      showCategory={true}
      categories={INCOME_CATEGORIES}
      t={t}
    />
  );

  return (
    <>
      <CollapsibleSection
        icon={DollarSign}
        title={t('frugal.incomes.title')}
        total={total}
        subtitle={subtitle}
        iconColorClass="text-emerald-400"
        showAddForm={showForm}
        onToggleAddForm={() => setShowForm(!showForm)}
        addForm={addFormComponent}
        defaultExpanded={false}
      >
        <div className="space-y-2">
          {incomes.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-4">{t('frugal.incomes.empty')}</p>
          ) : (
            incomes.map(income => (
              <ListItem
                key={income.id}
                item={income}
                colorClass="text-emerald-400"
                onToggle={handleToggle}
                onEdit={onEdit ? setEditingItem : undefined}
                onDelete={onDelete}
                showMenu={!!onEdit}
                t={t}
              />
            ))
          )}
        </div>
      </CollapsibleSection>

      {editingItem && (
        <EditItemModal
          item={editingItem}
          type="income"
          onSave={handleSaveEdit}
          onDelete={onDelete}
          onClose={() => setEditingItem(null)}
          showCategory={true}
          categories={INCOME_CATEGORIES}
          t={t}
        />
      )}

      {/* Account selector for depositing income */}
      <AccountSelector
        isOpen={!!pendingToggle}
        onClose={() => setPendingToggle(null)}
        onSelect={handleAccountSelect}
        amount={pendingToggle?.amount}
        description={pendingToggle?.name || pendingToggle?.source}
        token={token}
        t={t}
      />
    </>
  );
};

export default IncomesSection;
